import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import ErrorMessage from '../components/ErrorMessage'
import LoadingProgress from '../components/LoadingProgress'

import { fetchSale } from '../actions/sales'
import '../styles/SaleDetailsView.css'

// Fields from the XML Origin that are large/redundant and should be omitted from the transaction grid
const TPV_SKIP_FIELDS = new Set([
  'trx', 'data', 'ticket', 'ticketComercio', 'ticketHTML', 'ticketComercioHTML', 'display'
])

// Human-readable labels for common TPV XML fields
const TPV_LABELS = {
  status: 'Estado',
  pedido: 'Pedido',
  rts: 'RTS',
  contactless: 'Contactless',
  comercio: 'Comercio',
  terminal: 'Terminal',
  tarjeta: 'Tarjeta',
  titular: 'Titular',
  caducidad: 'Caducidad',
  operacion: 'Operación',
  aut: 'Autorización',
  fecha: 'Fecha TPV',
  etiquetaApp: 'Aplicación',
  aplicacion: 'AID',
  nTrans: 'Nº Trans.',
  resp: 'Respuesta',
  tvr: 'TVR',
  importe: 'Importe',
  tipoTasaAplicada: 'Tasa',
  literal: 'Literal',
}

const ITEM_NAMES = {
  BNS:   'Standard',
  CNS:   'Standard Carga',
  BWS:   'Standard XL',
  CWS:   'Standard Carga XL',
  BNM:   'Medium',
  CNM:   'Medium Carga',
  BWM:   'Medium XL',
  CWM:   'Medium Carga XL',
  BNL:   'Large',
  CNL:   'Large Carga',
  BWL:   'Large XL',
  CWL:   'Large Carga XL',
  BNXL:  'Large 4',
  CNXL:  'Large 4 Carga',
  BWXL:  'Large 4 XL',
  CWXL:  'Large 4 Carga XL',
  BN2XL: 'Large 5',
  CN2XL: 'Large 5 Carga',
  BW2XL: 'Large 5 XL',
  CW2XL: 'Large 5 Carga XL',
  BN3XL: 'Ski',
  CN3XL: 'Ski Carga',
  BW3XL: 'Ski XL',
  CW3XL: 'Ski Carga XL',
  XNS:   'Standard Cupón',
  XWS:   'Standard XL Cupón',
  XNM:   'Medium Cupón',
  XWM:   'Medium XL Cupón',
  XNL:   'Large Cupón',
  XWL:   'Large XL Cupón',
  XNXL:  'Large 4 Cupón',
  XWXL:  'Large 4 XL Cupón',
  XN2XL: 'Large 5 Cupón',
  XW2XL: 'Large 5 XL Cupón',
  XN3XL: 'Ski Cupón',
  XW3XL: 'Ski XL Cupón',
  // Baqueira
  '2PAX': '2 PAX',
  '3PAX': '3 PAX',
  '4PAX': '4 PAX',
  '3PAXSS': '3 PAX SS',
}

function getItemName(rawCode) {
  if (!rawCode) return rawCode
  const code = rawCode.replace(/^LOCKER/i, '')
  return ITEM_NAMES[code] || rawCode
}

// Maps language code → ISO 3166-1 alpha-2 country code for flagcdn.com
const LANG_COUNTRY = {
  ES: 'es', FR: 'fr', EN: 'gb', GB: 'gb',
  PT: 'pt', IT: 'it', DE: 'de', NL: 'nl',
  CA: 'es', RU: 'ru', ZH: 'cn', JA: 'jp',
  AR: 'sa', PL: 'pl', SV: 'se', NO: 'no',
  DA: 'dk', FI: 'fi', CS: 'cz', TR: 'tr',
}

function langFlagUrl(code) {
  if (!code) return null
  const cc = LANG_COUNTRY[code.toUpperCase()]
  return cc ? `https://flagcdn.com/32x24/${cc}.png` : null
}

function paymentBadgeClass(method) {
  if (!method) return 'other'
  switch (method.toUpperCase()) {
    case 'TPV': return 'tpv'
    case 'CASH': return 'cash'
    case 'QR': return 'qr'
    case 'PDA': return 'pda'
    default: return 'other'
  }
}

class SaleDetailsView extends React.Component {
  componentDidMount() {
    this.props.fetchSale(this.props.match.params.id)
  }

  parseXml(xmlString) {
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml')
    const data = {}
    const elements = xmlDoc.getElementsByTagName('*')
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i]
      if (el.childNodes.length === 1 && el.childNodes[0].nodeType === 3) {
        data[el.tagName] = el.textContent
      }
    }
    return data
  }

  extractTicketText(xmlString) {
    try {
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml')
      const ticketEl = xmlDoc.getElementsByTagName('ticket')[0]
      return ticketEl ? ticketEl.textContent : null
    } catch {
      return null
    }
  }

  render() {
    const { sale, error, loading } = this.props

    if (error) {
      return <ErrorMessage message={error.message} />
    }

    if (!sale || loading) {
      return <LoadingProgress />
    }

    // ── Date ──────────────────────────────────────────────────
    const saleDate = new Date(sale.TimeStamp)
    saleDate.setHours(saleDate.getHours() - 2)
    const formattedDate = `${saleDate.getDate().toString().padStart(2, '0')}/${(saleDate.getMonth() + 1).toString().padStart(2, '0')}/${saleDate.getFullYear()}  ${saleDate.getHours().toString().padStart(2, '0')}:${saleDate.getMinutes().toString().padStart(2, '0')}`

    const currency = (sale.Contract && sale.Contract.Currency) ? sale.Contract.Currency : 'EUR'

    const formattedAmount = parseFloat(sale.Amount).toLocaleString('es-ES', {
      style: 'currency',
      currency,
    })

    // ── TPV origin parsing ────────────────────────────────────
    let tpvData = null
    let ticketText = null

    if (sale.Origin && sale.PaymentMethod === 'TPV') {
      const trimmed = sale.Origin.trim()
      if (trimmed[0] === '{') {
        try { tpvData = JSON.parse(trimmed) } catch { /* ignore */ }
      } else if (trimmed[0] === '<') {
        try {
          tpvData = this.parseXml(trimmed)
          ticketText = this.extractTicketText(trimmed)
        } catch { /* ignore */ }
      }
    }

    // ── CASH details ──────────────────────────────────────────
    const processedDetails = sale.Details
      ? sale.Details.replace(/C:/g, 'Moneda:').replace(/B:/g, 'Billete:')
      : ''
    const processedPaidout = sale.Paidout
      ? sale.Paidout.replace(/C:/g, 'Moneda:').replace(/B:/g, 'Billete:')
      : ''

    // ── Helpers ───────────────────────────────────────────────
    const InfoRow = ({ icon, label, value, mono }) => (
      <div className="sd-info-row">
        <span className="sd-info-label">
          {icon && <i className={`fas fa-${icon}`} />}
          {label}
        </span>
        <span className={`sd-info-value${mono ? ' mono' : ''}`}>{value}</span>
      </div>
    )

    return (
      <div className="sale-details-view">

        {/* ── Header ── */}
        <div className="sd-header">
          <div className="sd-header-left">
            <h1>
              <i className="fas fa-receipt" />
              <span className="text-vw-light">VENTA </span>
              <span className="text-vw-dark">{sale.Code}</span>
            </h1>
            <div className="sd-subtitle">{formattedDate}</div>
          </div>
        </div>

        {/* ── KPI strip ── */}
        <div className="sd-kpi-strip">
          <div className="sd-kpi-card sd-kpi-accent">
            <div className="sd-kpi-icon-wrap"><i className="fas fa-euro-sign" /></div>
            <div className="sd-kpi-text">
              <div className="sd-kpi-label">Total</div>
              <div className="sd-kpi-value">{formattedAmount}</div>
            </div>
          </div>
          <div className="sd-kpi-card">
            <div className="sd-kpi-icon-wrap"><i className="fas fa-door-open" /></div>
            <div className="sd-kpi-text">
              <div className="sd-kpi-label">Taquilla</div>
              <div className="sd-kpi-value">{sale.Locker}</div>
            </div>
          </div>
          <div className="sd-kpi-card">
            <div className="sd-kpi-icon-wrap"><i className="fas fa-server" /></div>
            <div className="sd-kpi-text">
              <div className="sd-kpi-label">Máquina</div>
              <div className="sd-kpi-value">{(sale.Machine && sale.Machine.Serial) || '—'}</div>
            </div>
          </div>
          <div className="sd-kpi-card">
            {langFlagUrl(sale.Language)
              ? <img className="sd-kpi-flag-img" src={langFlagUrl(sale.Language)} alt={sale.Language} />
              : <div className="sd-kpi-icon-wrap"><i className="fas fa-language" /></div>
            }
            <div className="sd-kpi-text">
              <div className="sd-kpi-label">Idioma</div>
              <div className="sd-kpi-value">{sale.Language}</div>
            </div>
          </div>
          <div className={`sd-kpi-card sd-kpi-payment sd-kpi-pay-${paymentBadgeClass(sale.PaymentMethod)}`}>
            <div className="sd-kpi-icon-wrap">
              <i className={`fas fa-${sale.PaymentMethod === 'TPV' ? 'credit-card' : sale.PaymentMethod === 'CASH' ? 'coins' : sale.PaymentMethod === 'QR' ? 'qrcode' : 'mobile-alt'}`} />
            </div>
            <div className="sd-kpi-text">
              <div className="sd-kpi-label">Método de pago</div>
              <div className="sd-kpi-value">{sale.PaymentMethod}</div>
            </div>
          </div>
          {sale.OperationTime != null &&
            <div className="sd-kpi-card">
              <div className="sd-kpi-icon-wrap"><i className="fas fa-stopwatch" /></div>
              <div className="sd-kpi-text">
                <div className="sd-kpi-label">Tiempo op.</div>
                <div className="sd-kpi-value">{sale.OperationTime}s</div>
              </div>
            </div>
          }
        </div>

        {/* ── Main content grid ── */}
        <div className="sd-content-grid">

          {/* ── Left column ── */}
          <div className="sd-column">

            {/* Summary card */}
            <div className="sale-card">
              <div className="sd-card-header">
                <div className="sd-card-icon"><i className="fas fa-info-circle" /></div>
                <span className="sd-card-title">Resumen de la venta</span>
              </div>
              <div className="sd-card-body">
                <InfoRow icon="hashtag" label="ID" value={sale._id} mono />
                <InfoRow icon="calendar-alt" label="Fecha" value={formattedDate} />
                <InfoRow icon="building" label="Cliente" value={sale.Customer && sale.Customer.Fullname} />
                <InfoRow icon="file-invoice" label="Factura" value={sale.Code} mono />
                {sale.PaymentMethod === 'QR' && sale.RedsysCode &&
                  <InfoRow icon="link" label="Ref. TPV Virtual" value={sale.RedsysCode} mono />
                }
                {sale.WorkerSession && sale.WorkerSession !== '000000' &&
                  <InfoRow icon="user-tie" label="Sesión trabajador" value={sale.WorkerSession} />
                }
              </div>
            </div>

            {/* Customer / contact card */}
            {(sale.Phone || sale.IdNumber) &&
              <div className="sale-card">
                <div className="sd-card-header">
                  <div className="sd-card-icon"><i className="fas fa-user" /></div>
                  <span className="sd-card-title">Datos del usuario</span>
                </div>
                <div className="sd-card-body">
                  {sale.Phone &&
                    <InfoRow icon="phone" label="Teléfono" value={sale.Phone} />
                  }
                  {sale.IdNumber &&
                    <InfoRow icon="id-card" label="Nº Documento" value={sale.IdNumber} />
                  }
                </div>
              </div>
            }

          </div>

          {/* ── Right column ── */}
          <div className="sd-column">

            {/* Items card */}
            <div className="sale-card">
              <div className="sd-card-header">
                <div className="sd-card-icon"><i className="fas fa-shopping-cart" /></div>
                <span className="sd-card-title">Artículos vendidos</span>
              </div>
              <div className="sd-card-body" style={{ padding: 0 }}>
                <table className="sd-items-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Cant.</th>
                      <th style={{ textAlign: 'right' }}>Precio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sale.Items.map((item, idx) => (
                      <tr key={item._id || idx}>
                        <td>
                          <div className="sd-item-name">{getItemName(item.item)}</div>
                          <div className="sd-item-code">{item.item}</div>
                        </td>
                        <td>{item.qty}</td>
                        <td className="sd-item-price">{parseFloat(item.price).toFixed(2)} €</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} style={{ fontWeight: 700, color: '#495057' }}>Total</td>
                      <td className="sd-item-price">{formattedAmount}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

          </div>
        </div>

        {/* ── Transaction details (full width) ── */}
        {sale.PaymentMethod === 'TPV' && tpvData && (
          <div className="sale-card" style={{ marginBottom: 20 }}>
            <div className="sd-card-header">
              <div className="sd-card-icon"><i className="fas fa-credit-card" /></div>
              <span className="sd-card-title">Detalles de la transacción TPV</span>
            </div>
            <div className="sd-card-body">
              <div className="sd-trx-grid">
                {Object.entries(tpvData)
                  .filter(([key, value]) => !TPV_SKIP_FIELDS.has(key) && value != null && !Array.isArray(value) && String(value).trim() !== '')
                  .map(([key, value]) => (
                    <div className="sd-trx-item" key={key}>
                      <div className="sd-trx-key">{TPV_LABELS[key] || key}</div>
                      <div className="sd-trx-val">{String(value)}</div>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        )}

        {/* ── CASH details ── */}
        {sale.PaymentMethod === 'CASH' && (processedDetails || processedPaidout) && (
          <div className="sale-card" style={{ marginBottom: 20 }}>
            <div className="sd-card-header">
              <div className="sd-card-icon"><i className="fas fa-coins" /></div>
              <span className="sd-card-title">Detalles del pago en efectivo</span>
            </div>
            <div className="sd-card-body">
              <div className="sd-cash-grid">
                <div className="sd-cash-block">
                  <div className="sd-cash-title">Introducido</div>
                  <div className="sd-cash-value">{processedDetails || '—'}</div>
                </div>
                <div className="sd-cash-block">
                  <div className="sd-cash-title">Cambio</div>
                  <div className="sd-cash-value">{processedPaidout || '—'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    )
  }
}

const mapStateToProps = state => ({
  sale: state.sales.sale,
  loading: state.sales.loading,
  error: state.sales.error,
  auth: state.auth
})

const mapDispatchToProps = dispatch => ({
  fetchSale: bindActionCreators(fetchSale, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(SaleDetailsView)



